import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { publicApi } from "./api";

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const [content, setContent] = useState({});
  const [slides, setSlides] = useState([]);
  const [handovers, setHandovers] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [loaded, setLoaded] = useState(false);

  const refetch = useCallback(async () => {
    try {
      const [c, o, s, h] = await Promise.all([
        publicApi.getContent().catch(() => ({})),
        publicApi.getModelOverrides().catch(() => ({})),
        publicApi.listHeroSlides().catch(() => []),
        publicApi.listHandovers().catch(() => []),
      ]);
      setContent(c);
      setOverrides(o);
      setSlides(s);
      setHandovers(h);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refetch();
    const onFocus = () => refetch();
    const onContentBump = () => refetch();
    window.addEventListener("focus", onFocus);
    window.addEventListener("byd:content-bump", onContentBump);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("byd:content-bump", onContentBump);
    };
  }, [refetch]);

  const t = (key, fallback = "") => content[key] ?? fallback;

  return (
    <ContentContext.Provider value={{ content, slides, handovers, overrides, loaded, t, refetch }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be inside ContentProvider");
  return ctx;
}

/** Merge base model data with admin override (if any). */
export function applyOverride(baseModel, override) {
  if (!override) return baseModel;
  const mapped = {
    ...baseModel,
    name: override.name ?? baseModel.name,
    tagline: override.tagline ?? baseModel.tagline,
    category: override.category ?? baseModel.category,
    priceLabel: override.price_label ?? baseModel.priceLabel,
    stock: override.stock ?? baseModel.stock,
    hero: override.hero ?? baseModel.hero,
    thumb: override.thumb ?? baseModel.thumb,
    shortDesc: override.short_desc ?? baseModel.shortDesc,
    highlights:
      Array.isArray(override.highlights) && override.highlights.length > 0
        ? override.highlights
        : baseModel.highlights,
    keyFeatures:
      Array.isArray(override.key_features) && override.key_features.length > 0
        ? override.key_features.map((f) => ({ title: f.title, desc: f.desc }))
        : baseModel.keyFeatures,
    colors:
      Array.isArray(override.colors) && override.colors.length > 0
        ? override.colors
        : baseModel.colors,
    specs:
      override.specs && Object.keys(override.specs).length > 0
        ? override.specs
        : baseModel.specs,
  };
  return mapped;
}
